import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import aiAssistantService from '../../../services/aiAssistantService';
import { DASHBOARD_COLORS } from '../dashboardStyles';
import { getAiChatSuggestions } from '../../../utils/dashboardAiSuggestions';
import { getInsightInitialPrompt } from '../../../utils/dashboardInsightAi';

const AiChatDrawer = ({
  open,
  onClose,
  buildSnapshot,
  snapshotVersion,
  insightContext,
  onInsightContextHandled,
}) => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState(0);
  const [canChat, setCanChat] = useState(false);
  const [error, setError] = useState(null);
  const [snapshotStaleNotice, setSnapshotStaleNotice] = useState(false);
  const [lastUsage, setLastUsage] = useState(null);
  const [resetting, setResetting] = useState(false);
  const lastSnapshotVersion = useRef(null);
  const [activeInsight, setActiveInsight] = useState(null);
  const pendingInsightRef = useRef(null);
  const insightAutoSentRef = useRef(false);
  const sendMessageRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const refreshStatus = useCallback(async () => {
    try {
      const status = await aiAssistantService.getAiAssistantStatus();
      setCreditsRemaining(status.creditsRemaining ?? 0);
      setCanChat(Boolean(status.canChat));
      return status;
    } catch {
      return null;
    }
  }, []);

  const applyBootstrapData = (data, status) => {
    setSessionId(data.sessionId);
    setMessages(data.messages || []);
    setCreditsRemaining(data.creditsRemaining ?? status?.creditsRemaining ?? 0);
    setCanChat((data.creditsRemaining ?? 0) >= (status?.minCreditsPerMessage ?? 1));
    setLastUsage(null);
  };

  const handleNewChat = async () => {
    setResetting(true);
    setError(null);
    try {
      const status = await refreshStatus();
      const data = await aiAssistantService.resetAiAssistantChat();
      applyBootstrapData(data, status);
      setSnapshotStaleNotice(false);
      setActiveInsight(null);
      pendingInsightRef.current = null;
      insightAutoSentRef.current = false;
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Unable to start a new chat.');
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setMessages([]);
      setSessionId(null);
      setInput('');
      setError(null);
      setSnapshotStaleNotice(false);
      setActiveInsight(null);
      pendingInsightRef.current = null;
      insightAutoSentRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !insightContext) return;
    setActiveInsight(insightContext);
    pendingInsightRef.current = insightContext;
    insightAutoSentRef.current = false;
    onInsightContextHandled?.();
  }, [open, insightContext, onInsightContextHandled]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const init = async () => {
      setBootstrapping(true);
      setError(null);
      try {
        const status = await refreshStatus();
        if (cancelled) return;
        if (!status?.enabled) {
          setError('AI Assistant is not enabled for your company.');
          return;
        }
        const data = pendingInsightRef.current
          ? await aiAssistantService.resetAiAssistantChat()
          : await aiAssistantService.bootstrapAiAssistant();
        if (cancelled) return;
        applyBootstrapData(data, status);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Unable to start AI assistant.');
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [open, refreshStatus]);

  useEffect(() => {
    if (!open) return;
    if (lastSnapshotVersion.current != null && lastSnapshotVersion.current !== snapshotVersion) {
      setSnapshotStaleNotice(true);
    }
    lastSnapshotVersion.current = snapshotVersion;
  }, [open, snapshotVersion]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const suggestions = useMemo(() => {
    if (!open) return [];
    try {
      const snap = typeof buildSnapshot === 'function' ? buildSnapshot() : null;
      return getAiChatSuggestions({ snapshot: snap, messages, insightContext: activeInsight }, 4);
    } catch {
      return getAiChatSuggestions({ snapshot: null, messages, insightContext: activeInsight }, 4);
    }
  }, [open, buildSnapshot, snapshotVersion, messages, activeInsight]);

  const showSuggestions =
    !bootstrapping &&
    canChat &&
    !loading &&
    suggestions.length > 0 &&
    (messages.length === 0 ||
      (messages.length > 0 && messages[messages.length - 1]?.role === 'assistant'));

  const handleSend = useCallback(async (overrideText) => {
    const text = (typeof overrideText === 'string' ? overrideText : input).trim();
    if (!text || loading || !canChat) return;

    setLoading(true);
    setError(null);
    setInput('');
    setSnapshotStaleNotice(false);

    const optimistic = [...messages, { role: 'user', content: text }];
    setMessages(optimistic);

    try {
      const snapshot = buildSnapshot();
      const data = await aiAssistantService.sendAiAssistantMessage({
        message: text,
        sessionId,
        dashboardSnapshot: snapshot,
      });
      if (data.error) {
        setError(data.error);
        setMessages((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
        setCreditsRemaining(data.creditsRemaining ?? 0);
        await refreshStatus();
        return;
      }
      setSessionId(data.sessionId);
      setMessages(data.messages || optimistic);
      setCreditsRemaining(data.creditsRemaining ?? 0);
      setCanChat((data.creditsRemaining ?? 0) > 0);
      if (data.creditsUsed != null) {
        setLastUsage({
          credits: data.creditsUsed,
          tokens: data.tokensUsed,
          remaining: data.creditsRemaining ?? 0,
        });
      }
    } catch (err) {
      const apiError = err.response?.data?.error || err.response?.data?.message;
      if (err.response?.status === 402 || err.response?.data?.creditsRemaining === 0) {
        setCanChat(false);
        setCreditsRemaining(0);
        setError('You have no AI credits left. Purchase an AI Credits pack to continue.');
      } else {
        setError(apiError || 'Unable to get a response. Please try again.');
      }
      setMessages((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
      await refreshStatus();
    } finally {
      setLoading(false);
    }
  }, [
    input,
    loading,
    canChat,
    messages,
    sessionId,
    buildSnapshot,
    refreshStatus,
  ]);

  sendMessageRef.current = handleSend;

  useEffect(() => {
    if (!open || bootstrapping || !canChat || !pendingInsightRef.current || insightAutoSentRef.current) {
      return;
    }
    const ctx = pendingInsightRef.current;
    pendingInsightRef.current = null;
    insightAutoSentRef.current = true;
    let snap = null;
    try {
      snap = typeof buildSnapshot === 'function' ? buildSnapshot() : null;
    } catch {
      snap = null;
    }
    const prompt = getInsightInitialPrompt(ctx, snap);
    sendMessageRef.current?.(prompt);
  }, [open, bootstrapping, canChat, buildSnapshot, snapshotVersion]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (prompt) => {
    if (loading || !canChat) return;
    handleSend(prompt);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${DASHBOARD_COLORS.border}`,
          bgcolor: DASHBOARD_COLORS.surface,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesomeIcon sx={{ color: DASHBOARD_COLORS.accent, fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700} fontSize="1.05rem">
            Ask Gemini
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip
            size="small"
            label={`${creditsRemaining} credits`}
            color={creditsRemaining > 0 ? 'primary' : 'default'}
            variant={creditsRemaining > 0 ? 'filled' : 'outlined'}
          />
          <IconButton onClick={onClose} size="small" aria-label="Close AI chat">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: DASHBOARD_COLORS.bg }}>
        {bootstrapping && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!bootstrapping && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!bootstrapping && !canChat && !error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have no AI credits left. Go to Subscription → Add-ons to buy more via Razorpay.
          </Alert>
        )}

        {snapshotStaleNotice && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setSnapshotStaleNotice(false)}>
            Dashboard data was updated. Your next question will use the latest insights.
          </Alert>
        )}

        {lastUsage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLastUsage(null)}>
            Last reply used {lastUsage.credits} credit{lastUsage.credits !== 1 ? 's' : ''}
            {lastUsage.tokens != null ? ` (${lastUsage.tokens.toLocaleString()} tokens)` : ''}.{' '}
            {lastUsage.remaining != null
              ? `${lastUsage.remaining} credit${lastUsage.remaining !== 1 ? 's' : ''} remaining.`
              : `${creditsRemaining} credit${creditsRemaining !== 1 ? 's' : ''} remaining.`}
          </Alert>
        )}

        {!bootstrapping &&
          messages.map((msg, index) => (
            <Box
              key={`${msg.role}-${index}`}
              sx={{
                mb: 1.5,
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Box
                sx={{
                  maxWidth: '88%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: msg.role === 'user' ? DASHBOARD_COLORS.accent : DASHBOARD_COLORS.surface,
                  color: msg.role === 'user' ? '#fff' : DASHBOARD_COLORS.text,
                  border: msg.role === 'assistant' ? `1px solid ${DASHBOARD_COLORS.border}` : 'none',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                }}
              >
                {msg.content}
              </Box>
            </Box>
          ))}

        {loading && (
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Gemini is thinking…
            </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {showSuggestions && (
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: 0.5,
            bgcolor: DASHBOARD_COLORS.surface,
            borderTop: `1px solid ${DASHBOARD_COLORS.border}`,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {messages.some((m) => m.role === 'user') ? 'Continue with' : 'Suggested questions'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {suggestions.map((s) => (
              <Button
                key={s.label}
                variant="outlined"
                size="small"
                onClick={() => handleSuggestionClick(s.prompt)}
                sx={{
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  borderRadius: 999,
                  py: 0.75,
                  px: 1.5,
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  borderColor: DASHBOARD_COLORS.accent,
                  color: DASHBOARD_COLORS.accent,
                  '&:hover': {
                    borderColor: DASHBOARD_COLORS.accentHover,
                    bgcolor: 'rgba(102, 126, 234, 0.06)',
                  },
                }}
              >
                {s.label}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      <Divider />

      <Box sx={{ p: 2, bgcolor: DASHBOARD_COLORS.surface }}>
        {!canChat && !bootstrapping && (
          <Button
            fullWidth
            variant="outlined"
            sx={{ mb: 1.5, textTransform: 'none' }}
            onClick={() => {
              onClose();
              navigate('/subscription', { state: { openTab: 'addons' } });
            }}
          >
            Buy AI Credits
          </Button>
        )}
        <Button
          size="small"
          variant="text"
          startIcon={resetting ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={handleNewChat}
          disabled={bootstrapping || resetting || loading}
          sx={{ mb: 1, textTransform: 'none', color: DASHBOARD_COLORS.textMuted, fontWeight: 500 }}
        >
          New chat
        </Button>
        <Box display="flex" gap={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder={canChat ? 'Ask about your dashboard…' : 'No credits remaining'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading || bootstrapping || !canChat}
          />
          <IconButton
            color="primary"
            onClick={() => handleSend()}
            disabled={loading || bootstrapping || !canChat || !input.trim()}
            aria-label="Send message"
            sx={{ bgcolor: DASHBOARD_COLORS.accent, color: '#fff', '&:hover': { bgcolor: DASHBOARD_COLORS.accentHover }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AiChatDrawer;
